import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { useTranslation } from '../../hooks/useTranslation';
import { profileAPI } from '../../services/api';
import { Shield, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const SecurityTab = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: t('common.error'),
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    setChangingPassword(true);

    try {
      await profileAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast({
        title: t('common.success'),
        description: 'Password changed successfully',
      });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast({
        title: 'Confirmation requise',
        description: 'Veuillez taper SUPPRIMER pour confirmer',
        variant: 'destructive',
      });
      return;
    }

    setDeleting(true);

    try {
      await profileAPI.deleteAccount();
      toast({
        title: 'Compte supprimé',
        description: 'Toutes vos données ont été supprimées définitivement',
      });
      logout();
      navigate('/');
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to delete account',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Change Password */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <Shield className="h-5 w-5 text-primary-600" />
          <span>{t('profile.security.changePassword')}</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1">{t('profile.security.subtitle')}</p>

        <form onSubmit={handlePasswordSubmit} className="mt-6 space-y-4 max-w-md">
          <div>
            <Label htmlFor="current_password">{t('profile.security.currentPassword')}</Label>
            <Input
              id="current_password"
              name="current_password"
              type="password"
              value={passwordData.current_password}
              onChange={handlePasswordChange}
              className="input-premium mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="new_password">{t('profile.security.newPassword')}</Label>
            <Input
              id="new_password"
              name="new_password"
              type="password"
              value={passwordData.new_password}
              onChange={handlePasswordChange}
              className="input-premium mt-1"
              minLength={6}
              required
            />
          </div>

          <div>
            <Label htmlFor="confirm_password">{t('profile.security.confirmPassword')}</Label>
            <Input
              id="confirm_password"
              name="confirm_password"
              type="password"
              value={passwordData.confirm_password}
              onChange={handlePasswordChange}
              className="input-premium mt-1"
              minLength={6}
              required
            />
          </div>

          <Button type="submit" disabled={changingPassword} className="btn-premium">
            {changingPassword ? t('common.loading') : t('profile.security.updatePassword')}
          </Button>
        </form>
      </div>

      {/* Delete Account */}
      <div className="border-t pt-8">
        <h3 className="text-lg font-semibold text-red-600 flex items-center space-x-2">
          <Trash2 className="h-5 w-5" />
          <span>Supprimer mon compte (RGPD)</span>
        </h3>
        
        <Alert className="mt-4 border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-900">
            <strong>Attention :</strong> Cette action est irréversible et supprimera définitivement :
          </AlertDescription>
        </Alert>

        <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-sm font-medium text-slate-700 mb-3">Données qui seront supprimées :</p>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-red-500" />
              <span>Toutes vos factures créées</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-red-500" />
              <span>Tous vos documents scannés</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-red-500" />
              <span>Vos simulations PPF/PDP</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-red-500" />
              <span>Votre profil et informations entreprise</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-red-500" />
              <span>Votre abonnement Stripe (sera annulé)</span>
            </li>
          </ul>
        </div>

        {!showDeleteConfirm ? (
          <Button
            onClick={() => setShowDeleteConfirm(true)}
            variant="destructive"
            className="mt-4"
            data-testid="delete-account-btn"
          >
            Demander la suppression de mes données
          </Button>
        ) : (
          <div className="mt-4 space-y-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm font-medium text-red-800">
              Pour confirmer la suppression, tapez <strong>SUPPRIMER</strong> ci-dessous :
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="Tapez SUPPRIMER"
              className="border-red-300 focus:border-red-500 max-w-xs"
              data-testid="delete-confirm-input"
            />
            <div className="flex space-x-3">
              <Button
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'SUPPRIMER'}
                variant="destructive"
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Suppression en cours...' : 'Confirmer la suppression définitive'}
              </Button>
              <Button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                variant="outline"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityTab;
